/**
 * The rule sheet — `Builder planu - modal - progresja`'s `pane3`, and the first progression editor
 * this app has had.
 *
 * It composes six parts and owns none of their decisions: the head names the exercise and the
 * column, the sentence states and edits the rule, the matrix says what the plan prescribes, the
 * preview says what the rule would do, and the foot ends the session. Everything it knows about the
 * exercise comes from `lib/useProgressionSheet`.
 *
 * ── One sheet at a time, keyed by its slot token ────────────────────────────────────────────
 * `progressionDisclosure` holds which one is out, and the session component is mounted under a
 * `key` of that token — so a staged copy is seeded once per opening and a second exercise can never
 * inherit the first one's.
 *
 * ── It is a REAL dialog, and it covers the whole box ────────────────────────────────────────
 * It was a `role="group"` with a scrim painted over the day's stage, which left three things wrong
 * at once: Tab walked out of the pane and into the nav rail, the day's head and „Zwiń wszystkie"
 * stayed operable behind the dim, and the day's own head and foot were never covered at all. Base UI
 * supplies the focus trap, the `aria-modal` wiring, Escape and the outside press; every visual byte
 * is still ours.
 *
 * The portal's CONTAINER is an EMPTY box the host hands over, and the day's own content is a second
 * box beside it. Base UI portals to `<body>` by default, which would take the pane out of the day
 * and anchor its `position: absolute` to the page; `DayForm.module.css` carries the
 * `position: relative` on their shared parent that makes it the containing block.
 *
 * ── The day behind it is `inert`, and that is the modality ──────────────────────────────────
 * Until 14 September 2026 the dialog was portalled straight into the day form, beside the day's own
 * controls. Base UI's focus guards bracket the container's contents, so they bracketed the day too:
 * Tab from „Zastosuj do draftu" walked through the guards into `<body>`, the nav rail and the day
 * modal behind the scrim, where Enter on „Zamknij programowanie dnia" closed the day and threw the
 * staged rule away with no prompt — and every one of those controls sat under an `aria-hidden`
 * ancestor while it did it. Base UI hides the page behind a modal popup from assistive technology;
 * it does not take it out of the TAB ORDER, and it cannot inert an ancestor of its own portal.
 *
 * So the sheet does it: `inert` on the background for the session's lifetime, in a LAYOUT effect,
 * because the pencil this sheet hands focus back to lives inside that box and `focus()` inside an
 * `[inert]` subtree is refused silently. A layout cleanup lifts the attribute in the commit that
 * unmounts the sheet, before the passive effect that claims the focus runs.
 *
 * ── It carries its own live region, because the day's is not audible under it ────────────────
 * Base UI's modal takes the page behind the popup out of the accessibility tree, and the day dock's
 * `role="status"` is part of that page: measured on 14 September 2026, the dock's region held
 * „Panel progresji otwarty…" under an `aria-hidden` ancestor, so every word this sheet said while
 * it was open was said to nobody. It therefore renders one INSIDE the pane and says everything
 * through both. That is not the duplicate region the day dock's own header warns about: the two are
 * never in the accessibility tree at the same time, which is the whole reason this one exists.
 *
 * The two exits are the exception and stay on the HOST's region alone — „Zastosuj" and „Anuluj" are
 * said as the pane unmounts, and a region that goes away with its message announces nothing.
 *
 * ── Focus return stays the STORE's ──────────────────────────────────────────────────────────
 * `finalFocus={false}`, because two mechanisms would fight: `progressionDisclosure` names the slot
 * that was just closed and the card's own pencil claims focus (see `DayProgressionSlots`), which is
 * the one that survives a close routed through the discard prompt — by then Base UI's idea of "the
 * element that was focused when this opened" is the prompt's own button.
 *
 * ── Closing with an authored rule ASKS ──────────────────────────────────────────────────────
 * Escape, the press outside and „Anuluj" all go through `requestClose`, so a staged rule is never
 * thrown away silently. The prompt is `shared/ui/ConfirmDialog`, the app's one consequential
 * confirm — a second dialog component for this one question is the near-duplicate failure the house
 * rule exists to prevent.
 */

import { Dialog } from '@base-ui/react/dialog';
import { useEffect, useEffectEvent, useLayoutEffect, useRef, useState } from 'react';

import { axisForExercise, axisForField } from '~/entities/plan-draft';
import { useAnnouncer } from '~/shared/lib/useAnnouncer';
import type { MeasurementFieldKey } from '~/shared/measurement';
import { ConfirmDialog } from '~/shared/ui/ConfirmDialog';
import { LiveRegionAnnouncer } from '~/shared/ui/LiveRegionAnnouncer';

import { useProgressionDisclosure } from '../lib/progressionDisclosure';
import { useProgressionSheet } from '../lib/useProgressionSheet';
import { progressionMatrixView, type Translate } from '../model/matrixCellState';
import { stagedProjection } from '../model/progressionStaging';

import styles from './ProgressionSheet.module.css';
import { RuleSentence } from './RuleSentence';
import { SheetFoot } from './SheetFoot';
import { SheetHead } from './SheetHead';
import { SheetMatrix } from './SheetMatrix';
import { TrajectoryPreview } from './TrajectoryPreview';

const KEY = 'web:planBuilder.progression.sheet';

/**
 * Take a box out of play, or put it back.
 *
 * A plain function rather than two lines in the effect: the React Compiler's immutability rule
 * refuses a write to anything reached through a prop, and the box genuinely is the host's — this
 * says so at one place instead of opting the whole component out of the compiler with a disable.
 */
function setInert(element: HTMLElement | null, isInert: boolean): void {
  if (element !== null) element.inert = isInert;
}

export interface ProgressionSheetProps {
  /** The EMPTY box the pane is portalled into, and the day content it covers. See the header. */
  readonly container: React.RefObject<HTMLElement | null>;
  readonly background: React.RefObject<HTMLElement | null>;
  readonly t: Translate;
  readonly locale: string;
  /** Said out loud by the day dock's own live region — this slice owns no announcer. */
  readonly announce: (message: string) => void;
}

export function ProgressionSheet({
  container,
  background,
  t,
  locale,
  announce,
}: ProgressionSheetProps) {
  const openRuleSlot = useProgressionDisclosure((state) => state.openRuleSlot);
  if (openRuleSlot === null) return null;
  return (
    <SheetSession
      key={openRuleSlot}
      exerciseSlotToken={openRuleSlot}
      container={container}
      background={background}
      t={t}
      locale={locale}
      announce={announce}
    />
  );
}

function SheetSession({
  exerciseSlotToken,
  container,
  background,
  t,
  locale,
  announce,
}: {
  readonly exerciseSlotToken: string;
} & ProgressionSheetProps) {
  const closeRule = useProgressionDisclosure((state) => state.closeRule);
  const session = useProgressionSheet(exerciseSlotToken);
  const [showHints, setShowHints] = useState(false);
  /** True while the trainer is being asked whether an authored rule may be thrown away. */
  const [isAsking, setIsAsking] = useState(false);
  /* The cell keys are the GRID's, so the foot only prints them while the grid holds focus — see
   * `SheetFoot`. Escape outside the grid closes the sheet, which „oddaj regule" does not describe. */
  const [isGridFocused, setIsGridFocused] = useState(false);
  const paneRef = useRef<HTMLDivElement>(null);
  /* The pane's own region — see the header. `useAnnouncer` carries the repeat wrinkle, so the same
   * refusal pressed twice is said twice. */
  const inside = useAnnouncer();
  const sayInside = inside.announce;
  /** Said out loud while the sheet is OUT: both regions get it, one of them is in the tree. */
  const say = (message: string) => {
    sayInside(message);
    announce(message);
  };

  /* The day, out of play for as long as this session is mounted — see the header for why Base UI's
   * own modality is not enough and why this is a layout effect. */
  useLayoutEffect(() => {
    // Held in a local so the cleanup lifts the attribute off the SAME box this set it on.
    const covered = background.current;
    setInert(covered, true);
    return () => {
      setInert(covered, false);
    };
  }, [background]);

  const exerciseName = session?.stored.name ?? '';
  /* The day dock's region says what just opened. Nothing else announces it: the pane takes focus,
   * and a container taking focus is announced as its own label rather than as an event.
   * Said once per exercise, so the announcers are read through an effect event: the host's
   * `announce` is not promised a stable identity, and re-running this on every new one re-said
   * „opened" over whatever the pane had just said, a refusal included. */
  const sayOpened = useEffectEvent((opened: string) => {
    sayInside(opened);
    announce(opened);
  });
  useEffect(() => {
    if (exerciseName === '') return;
    sayOpened(t(`${KEY}.announce.opened`, { exercise: exerciseName }));
  }, [t, exerciseName]);

  if (session === null) return null;

  /* WHAT THE PLAN HOLDS, UNDER THE RULE BEING AUTHORED. Neither copy alone is the grid's subject:
   * the staged one was seeded when the sheet opened and carries the rule, the stored one carries the
   * pins and the roles written through it since. `stagedProjection` is where the two are read
   * together. */
  const grid = stagedProjection(session.stored, session.staged);

  /* ONE build of the grid per render. The caption's column, the key beside it, the phase note and
   * the grid itself all read the same answer; a second call was two builds and two places for an
   * input change to be made in only one. */
  const view = progressionMatrixView({
    exercise: grid,
    lifespan: session.lifespan,
    consumedCycles: session.consumedCycles,
    /* THE CHIPS' OWN COLUMN. The grid drew the plan's until 15 September 2026, under a note saying
     * so, and switching to Ciężar left a repetitions grid on screen under a sentence about
     * kilograms. The pin's address carries the same column, so the two can never be about different
     * fields — which is the whole reason the old reading existed. */
    field: session.field,
    /* WHAT THE CLIENT WAS GIVEN HERE AND WHAT THEY DID WITH IT, or `null` on a template. It rides on
     * the same `/full` read as the plan, so there is no second fetch, no join and no second loading
     * state — the grid's geometry is settled on the first frame either way. */
    live: session.live,
    t,
    locale,
  });

  const gridAxis =
    axisForField(session.field, grid.prescriptionMode) ?? axisForExercise(grid);
  /* A %1RM exercise progresses the PERCENT, which is not a measurement field and answers `null`
   * here — the raw key printed on screen until 14 September 2026. The set table's own name for that
   * column is what the trainer just typed under. */
  const columnName = (field: MeasurementFieldKey | null): string =>
    field === null
      ? t('web:planBuilder.day.field.percentOfOneRm')
      : t(`web:planBuilder.day.field.${field}`);

  const discard = () => {
    setIsAsking(false);
    closeRule();
    announce(t(`${KEY}.announce.cancelled`));
  };

  /** Every exit but „Zastosuj" — and every one of them asks first when a rule is staged. */
  const requestClose = () => {
    if (session.isDirty) setIsAsking(true);
    else discard();
  };

  return (
    <>
      <Dialog.Root
        open
        onOpenChange={(open) => {
          // Escape and the press outside both arrive here. While the prompt is up they must not:
          // Base UI's own dismissal on the prompt answers that press.
          if (!open && !isAsking) requestClose();
        }}
      >
        <Dialog.Portal container={container}>
          <Dialog.Backdrop className={styles.scrim} />
          <Dialog.Popup
            ref={paneRef}
            className={styles.sheet}
            aria-label={t(`${KEY}.caption`, { exercise: session.stored.name })}
            /* The PANE, not the first control in it: landing on the family menu announces that
             * control as though it had been chosen. */
            initialFocus={paneRef}
            finalFocus={false}
            tabIndex={-1}
          >
            <LiveRegionAnnouncer message={inside.message} />

            <div className={styles.scroll}>
              <SheetHead
                exercise={session.staged}
                exerciseName={session.stored.name}
                field={session.field}
                onSelectColumn={session.selectColumn}
                showHints={showHints}
                onToggleHints={() => {
                  setShowHints((shown) => !shown);
                }}
                t={t}
              />

              <RuleSentence
                staged={session.staged}
                field={session.field}
                dispatch={session.dispatch}
                onBandEdit={session.noteBandEdit}
                showHints={showHints}
                t={t}
                locale={locale}
              />

              {/* The grid's own column, NAMED — the one the chips above have selected, since the
                * grid follows them. „MACIERZ · CIĘŻAR" is the mock's own caption. */}
              <div className={styles.sectionRow}>
                <span className={styles.sectionLabel}>
                  {t(`${KEY}.matrix.title`, { column: columnName(gridAxis.field) })}
                </span>
                <span className={styles.spring} />
                {showHints && (
                  <span className={styles.legend}>
                    {view.legend.map((entry) => (
                      /* The mock's own word on the key, the sentence on `title`: a one-word key is
                        * readable beside the grid it explains and useless on its own, and the
                        * sentence is what every cell's own accessible name already says. */
                      <span
                        key={entry.state}
                        className={styles.legendItem}
                        title={entry.description}
                      >
                        <span
                          className={styles.swatch}
                          data-state={entry.state}
                          aria-hidden="true"
                        />
                        {entry.label}
                      </span>
                    ))}
                  </span>
                )}
              </div>

              <SheetMatrix
                view={view}
                exercise={grid}
                /* The column a pin lands in — the one the grid just drew. */
                field={session.field}
                /* The exercise's FIRST active cycle. `setMatrixOverride` refuses a pin on it — that
                 * column is the authored base, and pinning it would store the plan's starting value
                 * as a correction to itself. */
                baseCycle={session.lifespan.cycles[0] ?? 1}
                onFocusWithinChange={setIsGridFocused}
                announce={say}
                t={t}
              />

              {showHints && <span className={styles.hint}>{view.note}</span>}

              <TrajectoryPreview
                staged={session.staged}
                field={session.field}
                /* The CHART's own labels, which suffix „· baza" and „· teraz" in the mock's lower
                  * case and say nothing under a cycle that is merely done. On a template they are
                  * the bare `C1 … C5` this surface has always drawn. */
                cycleLabels={view.columns.map((column) => column.chartLabel)}
                cycles={view.columns.map((column) => column.cycleIndex)}
                live={session.live}
                nowIndex={view.nowColumn?.position ?? null}
                showHints={showHints}
                t={t}
                locale={locale}
              />
            </div>

            <SheetFoot
              stored={session.stored}
              isDirty={session.isDirty}
              showKeys={isGridFocused}
              onClearPins={() => {
                say(t(`${KEY}.announce.pinsCleared`));
              }}
              onCancel={requestClose}
              onApply={() => {
                session.apply();
                closeRule();
                announce(t(`${KEY}.announce.applied`));
              }}
              t={t}
            />
          </Dialog.Popup>
        </Dialog.Portal>
      </Dialog.Root>

      <ConfirmDialog
        prompt={
          isAsking
            ? {
                title: t(`${KEY}.discard.title`),
                subject: session.stored.name,
                description: t(`${KEY}.discard.body`),
                confirmLabel: t(`${KEY}.discard.confirm`),
                cancelLabel: t(`${KEY}.discard.keep`),
                destructiveConfirm: true,
              }
            : null
        }
        onConfirm={discard}
        onCancel={() => {
          setIsAsking(false);
        }}
      />
    </>
  );
}
